import { z } from "zod";
import { IEntityItem } from "./entitiesDefinitions";
import { defineNullableOrMaxLength, defineNullableOrMaxNumber, defineOptionalNullableBoolean, defineRequiredMaxLength } from "@lib/definitions";
import { FormState } from "./definitions";
import { IApprovalRequestInfo } from "./approvalRequestDefinitions";

export interface IIssuerItem {
    id: number;
    clientCode?: string;
    issuerCode?: string;
    name?: string;
    status?: string;
    statusName?: string;
    coStatusName?: string;
    approvalRequestId?: number
    isRequestPublish: boolean;
    type?: string;
    description: string;
    entityId: number | null;
    issuerType: string;
    entity: IEntityItem;

    holderName?: string;
    holderState?: string;
    dateOfIncorporationDate?: Date;
    dateOfIncorporation?: string;
    stateCodeOfIncorporation?: string;
    sicCode?: string;
    naicsCode?: string;
    lastYearFiscalRevenue?: number;
    totalAssets?: number;
    numberOfEmployees?: number;
    isTestOrganization?: boolean | null;
    isCardCo?: boolean | null;
    draftId?: number;
    liveId?: number;

    isDraft?: boolean;
    propagateChangesToExistingRelatedProgramsFlag?: boolean;

    //Others
    generalInfo?: IIssuerGeneralInfo;
    escheatInfo?: IIssuerEscheatInfo;
    inheritedEscheatInfo?: IIssuerEscheatInfo;
    contactInfos?: IContactInfo[];
    contactInfo?: IContactInfo[];
    addresses?: IAddressInfo[];
    approvalRequestInfo?: IApprovalRequestInfo;
};

export interface IIssuerGeneralInfo {
    issuerType: string;
    holderName?: string; //* required
    holderState?: string; //* required
    dateOfIncorporation?: string;
    stateCodeOfIncorporation?: string;
    sicCode?: string;
    naicsCode?: string;
    lastYearFiscalRevenue?: number;
    totalAssets?: number;
    numberOfEmployees?: number;
    isTestOrganization?: boolean | null;
    isCardCo?: boolean | null;
};

export const defaultIssuerFormControlProperties = {
    xsFormControl: 6,
    mdFormControl: 3,
}

export const IssuerGeneralInfoValidationSchema = z.object({
    issuerType: z.string().nullish(),
    holderName: defineRequiredMaxLength("holder name", 100),
    holderState: defineRequiredMaxLength("holder state", 100),
    dateOfIncorporation: z.string().nullish(),
    stateCodeOfIncorporation: z.string().nullish(),
    sicCode: defineNullableOrMaxLength("SIC Code", 4),
    naicsCode: defineNullableOrMaxLength("NAICS Code", 8),
    lastYearFiscalRevenue: defineNullableOrMaxNumber("Last Year Fiscal Revenue", 1000000000000),
    totalAssets: defineNullableOrMaxNumber("Total Assets", 1000000000000),
    numberOfEmployees: defineNullableOrMaxNumber("Number of Employees", 2000000000),
    isTestOrganization: defineOptionalNullableBoolean("is test organization"),
    isCardCo: defineOptionalNullableBoolean("is CardCo"),
});

export const DraftIssuerGeneralInfoValidationSchema = z.object({
    issuerType: z.string().nullish(),
    holderName: defineNullableOrMaxLength("holder name", 100),
    holderState: defineNullableOrMaxLength("holder state", 100),
    dateOfIncorporation: z.string().nullish(),
    stateCodeOfIncorporation: z.string().nullish(),
    sicCode: defineNullableOrMaxLength("SIC Code", 4),
    naicsCode: defineNullableOrMaxLength("NAICS Code", 8),
    lastYearFiscalRevenue: defineNullableOrMaxNumber("Last Year Fiscal Revenue", 1000000000000),
    totalAssets: defineNullableOrMaxNumber("Total Assets", 1000000000000),
    numberOfEmployees: defineNullableOrMaxNumber("Number of Employees", 2000000000),
    isTestOrganization: defineOptionalNullableBoolean("is test organization"),
    isCardCo: defineOptionalNullableBoolean("is CardCo"),
});

export const ValidateIssuerGeneralInfoValidationSchema = (data?: IIssuerGeneralInfo) => {
    if (data) {
        data.lastYearFiscalRevenue = Number(data.lastYearFiscalRevenue);
        data.totalAssets = Number(data.totalAssets);
        data.numberOfEmployees = Number(data.numberOfEmployees);
    }
    return IssuerGeneralInfoValidationSchema.safeParse(data);
};

export const ValidateDraftIssuerGeneralInfoValidationSchema = (data?: IIssuerGeneralInfo) => {
    if (data) {
        data.lastYearFiscalRevenue = Number(data.lastYearFiscalRevenue);
        data.totalAssets = Number(data.totalAssets);
        data.numberOfEmployees = Number(data.numberOfEmployees);
    }
    return DraftIssuerGeneralInfoValidationSchema.safeParse(data);
};

export interface IIssuerEscheatInfo {
    riskStatesPossMultiRetailExemptions: IPossMultiRetailExemptionItem[];
    allowDeemedRegistered?: boolean;
    applyZipOrStateAsDeliverableAddress?: boolean;
    client1stPriorityTo2ndPriorityStateImplicit?: boolean;
    client1stPriorityTo2ndPriorityStateExplicit?: boolean;
    applyThirdPriority?: boolean;
    ignoreThirdPriorityStatutoryAuthority?: boolean;
    isClient2ndPriorityExemptTo3rdPriorityOverride?: boolean;
    applyCashBackImpose?: boolean;
    primarilySellingTangiblePropertyAtRetail?: boolean;
    applyRegulatoryAuthorityEscheatRule?: boolean;
    applyRegulatoryAuthorityEscheatExpiry?: boolean;
    applyTheNoConsiderationExemption?: boolean;
    applyTheMonetaryConsiderationExemption?: boolean;
    applyTheNonMonetaryConsiderationExemption?: boolean;
    applyTheExpirationDateEscheatExemption?: boolean;
    applyTheDerivativeRightsExemption?: boolean;
    applyTheReversionaryInterestExemption?: boolean;
    bulkSales?: boolean;
    allowAggregateFiling?: boolean;
    approvedExceptionVariance?: number;
    doNegativeFilingIfRequired?: boolean;
    doNegativeFilingIfPermitted?: boolean;
    doNotDoNegativeFiling?: boolean;

    //Others
    issuerId?: number;
    issuerCombinedId?: string;

    //Program attributes
    prepaidRedemptionRate?: number;
    tradeMarkLicensingRate?: number;
    handlingFeeRate?: number;
    escheatUnderwritingFactor?: number;
    cashFactor?: number;
    payment?: string;
    cash?: number;
};


export const issuerTypes = [
    "Bank Issuer",
    "Retail Issuer",
    "Test Issuer",
    "Third Party Issuer"
]

export interface IPossMultiRetailExemptionItem {
    issuerCode?: string;
    stateId: string;
    firstFilingDate?: string;
}

export const DEFAULT_RISK_STATES = [
    { stateId: "CA", firstFilingDate: undefined },
    { stateId: "MA", firstFilingDate: undefined },
    { stateId: "MD", firstFilingDate: undefined },
    { stateId: "MI", firstFilingDate: undefined },
    { stateId: "MN", firstFilingDate: undefined },
    { stateId: "MT", firstFilingDate: undefined },
    { stateId: "RI", firstFilingDate: undefined },
    { stateId: "UT", firstFilingDate: undefined },
    { stateId: "WA", firstFilingDate: undefined },
]

export interface IRelatedProgramConflictCheckItem {
    clientCode: string;
    programCode: string;
    isStaging: boolean;
}

export interface IExistingContactFoundItem {
    attribute: string;
    value: string | null;
}

export interface IContactInfo {
    id: number;
    email: string | null;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    title: string | null;
    technical: boolean | null;
    billing: boolean | null;
    functional: boolean | null;
    associatedUserId: number | null;
    associatedUserInfo: IAssociatedUserInfo | null;

    index: number | null;
    formState?: any;
    isNewlyCreated?: boolean;
    isNewlyAssociated?: boolean;
    listRequiredFields?: string[];
}

export interface IAddressInfo {
    id: number;
    address: string;
    city: string;
    state: string;
    zip: string;
    principalPlaceOfBusiness: boolean;
    filing: boolean;
    legal: boolean;
    index: number | null;
    addressIndex: number;
    formState: FormState | null;
    listRequiredFields: string[] | undefined;

    isNewlyCreated?: boolean;
}

export interface IAssociatedUserInfo {
    id: number;
    emailAddress: string;
    firstName: string;
    lastName: string;
    status: string;
    roles: IAssociatedUserRoleInfo[];
}

export interface IAssociatedUserRoleInfo {
    id: number;
    name: string;
    description: string;
}

export interface IModifyContactDto {
    issuerId: number;
    isDraft: boolean;
    contactInfo: IContactInfo;
    prevContactId: string;
}

export interface IModifyContactResultDto {
    contactId: number;
    issuerId: number;
    isDraft: boolean;
    isHasExistingMapping: boolean;

}

export interface IModifyAddressDto {
    issuerId: number;
    isDraft: boolean;
    addressInfo: IAddressInfo;
    prevAddressId: string;
}

export interface IModifyAddressResultDto {
    addressId: number;
    issuerId: number;
    isDraft: boolean;
    isHasExistingMapping: boolean;

}
